import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Gear,
  CloudArrowUp,
  CheckCircle,
  XCircle,
  Warning,
  ArrowClockwise,
  Eye,
  EyeSlash,
  FloppyDisk,
  Play,
  Plugs,
  PlugsConnected,
  Info,
  Camera,
  Circle,
  CheckSquare,
  Square,
  MinusSquare,
  MagnifyingGlass,
  VideoCamera,
  UserPlus,
  Trash,
  ToggleLeft,
  ToggleRight,
  Users,
  Database,
  Timer,
  Bell,
  Broom,
  ChartBar,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/store';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BridgeSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const token = localStorage.getItem('4sat_token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Devices config for mediaUrl
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [selectedLiveVideo, setSelectedLiveVideo] = useState(new Set());
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [savingDevices, setSavingDevices] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [liveVideoSearch, setLiveVideoSearch] = useState('');

  // Tracksolid accounts management
  const [tracksolidAccounts, setTracksolidAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ email: '', password: '', name: '' });
  const [addingAccount, setAddingAccount] = useState(false);

  // Device video configurations (per-device settings)
  const [deviceVideoConfigs, setDeviceVideoConfigs] = useState({});
  const [videoConfigSearch, setVideoConfigSearch] = useState('');
  const [savingVideoConfig, setSavingVideoConfig] = useState(null);

  // Bridge config
  const [config, setConfig] = useState({
    email: '',
    password: '',
    base_url: 'https://4sat.cl/api',
    enabled: true,
    batch_size: 10,
    batch_timeout: 5.0,
    test_imei: '864993060374437',
    allow_audio_control: false,  // Control de audio en streaming externo
    allowed_channels: [0, 1, 2, 3],  // Canales permitidos
  });

  // Bridge status
  const [status, setStatus] = useState(null);

  // Pool telemetry state
  const [poolStats, setPoolStats] = useState(null);
  const [poolConfig, setPoolConfig] = useState({
    max_pool_size_mb: 8192,
    retention_hours: 24,
    min_records_per_minute: 6,
    record_interval_seconds: 10,
    alert_threshold_percent: 10,
    max_track_calls_per_cycle: 10,
    enabled_alert_types: [],
  });
  const [availableAlertTypes, setAvailableAlertTypes] = useState([]);
  const [alertTranslations, setAlertTranslations] = useState({});
  const [loadingPool, setLoadingPool] = useState(false);
  const [savingPool, setSavingPool] = useState(false);
  const [cleaningPool, setCleaningPool] = useState(false);

  // Port configuration state
  const [portConfig, setPortConfig] = useState({
    defaultPort: 6055,
    accountPorts: [],
    imeiOverrides: [],
    availablePorts: [],
  });
  const [loadingPortConfig, setLoadingPortConfig] = useState(false);
  const [savingPortConfig, setSavingPortConfig] = useState(false);
  const [newImeiOverride, setNewImeiOverride] = useState({ imei: '', port: 9090 });

  useEffect(() => {
    if (token) {
      loadConfig();
      loadStatus();
      loadDevices();
      loadTracksolidAccounts();
      loadDeviceVideoConfigs();
      loadPoolStats();
      loadPoolConfig();
      loadAlertTypes();
      loadPortConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Load pool statistics
  const loadPoolStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pool/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPoolStats(response.data);
    } catch (err) {
      console.error('Error loading pool stats:', err);
    }
  };

  // Load pool configuration
  const loadPoolConfig = async () => {
    setLoadingPool(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pool/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPoolConfig(response.data);
    } catch (err) {
      console.error('Error loading pool config:', err);
    } finally {
      setLoadingPool(false);
    }
  };

  // Load available alert types
  const loadAlertTypes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pool/alert-types`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableAlertTypes(response.data.alert_types || []);
      setAlertTranslations(response.data.translations || {});
    } catch (err) {
      console.error('Error loading alert types:', err);
    }
  };

  // Load port configuration
  const loadPortConfig = async () => {
    setLoadingPortConfig(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bridge/port-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPortConfig(response.data);
    } catch (err) {
      console.error('Error loading port config:', err);
    } finally {
      setLoadingPortConfig(false);
    }
  };

  // Update account default port
  const updateAccountPort = async (email, port) => {
    setSavingPortConfig(true);
    try {
      await axios.put(`${BACKEND_URL}/api/bridge/port-config/account`, 
        { email, port: parseInt(port) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadPortConfig();
    } catch (err) {
      console.error('Error updating account port:', err);
      alert('Error al actualizar puerto de cuenta');
    } finally {
      setSavingPortConfig(false);
    }
  };

  // Add IMEI port override
  const addImeiOverride = async () => {
    if (!newImeiOverride.imei) {
      alert('Ingrese un IMEI');
      return;
    }
    setSavingPortConfig(true);
    try {
      await axios.put(`${BACKEND_URL}/api/bridge/port-config/imei`,
        { 
          imei: newImeiOverride.imei, 
          port: parseInt(newImeiOverride.port),
          deviceName: devices.find(d => d.imei === newImeiOverride.imei)?.name || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewImeiOverride({ imei: '', port: 9090 });
      loadPortConfig();
    } catch (err) {
      console.error('Error adding IMEI override:', err);
      alert('Error al agregar excepción');
    } finally {
      setSavingPortConfig(false);
    }
  };

  // Remove IMEI port override
  const removeImeiOverride = async (imei) => {
    if (!confirm(`¿Eliminar excepción para IMEI ${imei}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/bridge/port-config/imei/${imei}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadPortConfig();
    } catch (err) {
      console.error('Error removing IMEI override:', err);
    }
  };

  // Save pool configuration
  const savePoolConfig = async () => {
    setSavingPool(true);
    try {
      await axios.put(`${BACKEND_URL}/api/pool/config`, poolConfig, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Configuración del pool guardada');
      loadPoolStats();
    } catch (err) {
      console.error('Error saving pool config:', err);
      alert('Error al guardar configuración del pool');
    } finally {
      setSavingPool(false);
    }
  };

  // Trigger pool cleanup
  const triggerPoolCleanup = async (force = false) => {
    if (force && !confirm('¿Está seguro de eliminar TODOS los registros enviados del pool?')) {
      return;
    }
    setCleaningPool(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/pool/cleanup`, { force }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(`Limpieza completada: ${response.data.deleted_count} registros eliminados`);
      loadPoolStats();
    } catch (err) {
      console.error('Error cleaning pool:', err);
      alert('Error al limpiar el pool');
    } finally {
      setCleaningPool(false);
    }
  };

  // Load device video configurations
  const loadDeviceVideoConfigs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/video/device-configs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Convert array to object keyed by imei
      const configsObj = {};
      (response.data.configs || []).forEach(cfg => {
        configsObj[cfg.imei] = cfg;
      });
      setDeviceVideoConfigs(configsObj);
    } catch (err) {
      console.error('Error loading device video configs:', err);
    }
  };

  // Save device video config
  const saveDeviceVideoConfig = async (imei, audioEnabled, channels) => {
    setSavingVideoConfig(imei);
    try {
      await axios.put(
        `${BACKEND_URL}/api/video/device-config/${imei}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            allow_audio_control: audioEnabled,
            allowed_channels: channels.join(','),
          },
        }
      );
      // Update local state
      setDeviceVideoConfigs(prev => ({
        ...prev,
        [imei]: {
          imei,
          allow_audio_control: audioEnabled,
          allowed_channels: channels,
        }
      }));
    } catch (err) {
      console.error('Error saving device video config:', err);
      alert('Error al guardar configuración');
    } finally {
      setSavingVideoConfig(null);
    }
  };

  // Reset device video config to defaults
  const resetDeviceVideoConfig = async (imei) => {
    setSavingVideoConfig(imei);
    try {
      await axios.delete(`${BACKEND_URL}/api/video/device-config/${imei}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Remove from local state
      setDeviceVideoConfigs(prev => {
        const newConfigs = { ...prev };
        delete newConfigs[imei];
        return newConfigs;
      });
    } catch (err) {
      console.error('Error resetting device video config:', err);
    } finally {
      setSavingVideoConfig(null);
    }
  };

  // Load Tracksolid accounts
  const loadTracksolidAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tracksolid/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTracksolidAccounts(response.data.accounts || []);
    } catch (err) {
      console.error('Error loading Tracksolid accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Add new Tracksolid account
  const handleAddAccount = async () => {
    if (!newAccount.email || !newAccount.password) {
      setTestResult({ success: false, message: 'Email y contraseña son requeridos' });
      return;
    }
    
    setAddingAccount(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/tracksolid/accounts`,
        newAccount,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult({ success: true, message: `Cuenta ${newAccount.email} agregada exitosamente` });
      setNewAccount({ email: '', password: '', name: '' });
      setShowAddAccount(false);
      await loadTracksolidAccounts();
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Error agregando cuenta' 
      });
    } finally {
      setAddingAccount(false);
    }
  };

  // Delete Tracksolid account
  const handleDeleteAccount = async (email) => {
    if (!window.confirm(`¿Eliminar la cuenta ${email}?`)) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/tracksolid/accounts/${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult({ success: true, message: `Cuenta ${email} eliminada` });
      await loadTracksolidAccounts();
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Error eliminando cuenta' 
      });
    }
  };

  // Toggle Tracksolid account enabled/disabled
  const handleToggleAccount = async (email, currentEnabled) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/tracksolid/accounts/${encodeURIComponent(email)}/toggle`,
        { enabled: !currentEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadTracksolidAccounts();
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Error actualizando cuenta' 
      });
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bridge/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig((prev) => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error('Error loading config:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bridge/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(response.data);
    } catch (err) {
      console.error('Error loading status:', err);
    }
  };

  const loadDevices = async () => {
    setLoadingDevices(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bridge/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDevices(response.data.devices || []);
      // Inicializar los dispositivos seleccionados para mediaUrl
      const enabledMedia = response.data.devices
        .filter((d) => d.sendMediaUrl)
        .map((d) => d.imei);
      setSelectedDevices(new Set(enabledMedia));
      // Inicializar los dispositivos seleccionados para liveVideo
      const enabledLiveVideo = response.data.devices
        .filter((d) => d.sendLiveVideo)
        .map((d) => d.imei);
      setSelectedLiveVideo(new Set(enabledLiveVideo));
    } catch (err) {
      console.error('Error loading devices:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  const toggleDevice = (imei) => {
    setSelectedDevices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imei)) {
        newSet.delete(imei);
      } else {
        newSet.add(imei);
      }
      return newSet;
    });
  };

  const toggleLiveVideo = (imei) => {
    setSelectedLiveVideo((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imei)) {
        newSet.delete(imei);
      } else {
        newSet.add(imei);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedDevices(new Set(devices.map((d) => d.imei)));
  };

  const deselectAll = () => {
    setSelectedDevices(new Set());
  };

  const selectAllLiveVideo = () => {
    setSelectedLiveVideo(new Set(devices.map((d) => d.imei)));
  };

  const deselectAllLiveVideo = () => {
    setSelectedLiveVideo(new Set());
  };

  const saveDevicesConfig = async () => {
    setSavingDevices(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/bridge/devices`,
        { 
          enabledMediaUrl: Array.from(selectedDevices),
          enabledLiveVideo: Array.from(selectedLiveVideo),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult({
        success: true,
        message: `Configuración guardada: ${selectedDevices.size} para evidencia, ${selectedLiveVideo.size} para video en vivo`,
      });
      // Recargar la lista
      await loadDevices();
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Error guardando configuración',
      });
    } finally {
      setSavingDevices(false);
    }
  };

  // Filtrar dispositivos por búsqueda
  const filteredDevices = devices.filter(
    (d) =>
      d.name.toLowerCase().includes(deviceSearch.toLowerCase()) ||
      d.imei.includes(deviceSearch)
  );

  const filteredLiveVideoDevices = devices.filter(
    (d) =>
      d.name.toLowerCase().includes(liveVideoSearch.toLowerCase()) ||
      d.imei.includes(liveVideoSearch)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      // Solo enviar password si se modificó
      if (!payload.password) {
        delete payload.password;
      }

      await axios.put(`${BACKEND_URL}/api/bridge/config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Recargar status
      await loadStatus();
      setTestResult({ success: true, message: 'Configuración guardada exitosamente' });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Error guardando configuración',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/bridge/test`,
        { imei: config.test_imei },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(response.data);
      // Recargar status
      await loadStatus();
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Error en prueba de conexión',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <ArrowClockwise size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} />
          <span>Volver al Dashboard</span>
        </button>
        <div className="flex-1 flex justify-center">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <CloudArrowUp size={24} className="text-blue-600" />
            Configuración del Bridge 4Sat
          </h1>
        </div>
        <div className="w-32" />
      </header>

      <main className="max-w-4xl mx-auto py-8 px-6">
        {/* Bridge Status Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="bridge-status-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <PlugsConnected size={24} className="text-blue-600" />
              Estado del Servicio
            </h2>
            <button
              onClick={loadStatus}
              className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-colors"
              title="Actualizar estado"
              data-testid="refresh-status-btn"
            >
              <ArrowClockwise size={18} />
            </button>
          </div>

          {status && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {status.positions_processed || 0}
                </div>
                <div className="text-sm text-gray-500">Posiciones procesadas</div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-amber-400">
                  {status.alerts_processed || 0}
                </div>
                <div className="text-sm text-gray-500">Alertas procesadas</div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="text-2xl font-bold text-emerald-400">
                  {status.dispatcher?.positions_sent || 0}
                </div>
                <div className="text-sm text-gray-500">Enviadas a 4sat.cl</div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  {status.enabled ? (
                    <>
                      <PlugsConnected size={24} className="text-emerald-400" />
                      <span className="text-emerald-400 font-medium">Activo</span>
                    </>
                  ) : (
                    <>
                      <Plugs size={24} className="text-gray-500" />
                      <span className="text-gray-500 font-medium">Inactivo</span>
                    </>
                  )}
                </div>
                <div className="text-sm text-gray-500">Estado del servicio</div>
              </div>
            </div>
          )}

          {status?.dispatcher && (
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                {status.dispatcher.authenticated ? (
                  <CheckCircle size={16} className="text-emerald-400" />
                ) : (
                  <XCircle size={16} className="text-red-400" />
                )}
                <span className="text-gray-500">
                  {status.dispatcher.authenticated ? 'Autenticado en 4sat.cl' : 'Sin autenticar'}
                </span>
              </div>
              <div className="text-gray-500">
                OsmAnd: {status.dispatcher.osmand_host}:{status.dispatcher.osmand_port}
              </div>
              {status.dispatcher.errors > 0 && (
                <div className="flex items-center gap-2 text-red-400">
                  <Warning size={16} />
                  <span>{status.dispatcher.errors} errores</span>
                </div>
              )}
              {status.dispatcher.last_dispatch && (
                <div className="text-gray-500">
                  Último envío: {new Date(status.dispatcher.last_dispatch).toLocaleString('es-CL')}
                </div>
              )}
            </div>
          )}
          
          {status?.dispatcher?.last_error && (
            <div className="mt-3 p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
              <div className="flex items-start gap-2 text-sm">
                <Warning size={16} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <span className="text-red-300 font-medium">Último error: </span>
                  <span className="text-red-200/80">{status.dispatcher.last_error}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Configuration Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6" data-testid="bridge-config-form">
          <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
            <Gear size={24} className="text-gray-500" />
            Configuración de Conexión
          </h2>

          <div className="space-y-6">
            {/* Credenciales 4sat.cl */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Email de 4sat.cl
                </label>
                <input
                  type="email"
                  value={config.email}
                  onChange={(e) => setConfig({ ...config, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="usuario@4sat.cl"
                  data-testid="bridge-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Contraseña de 4sat.cl
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={config.password}
                    onChange={(e) => setConfig({ ...config, password: e.target.value })}
                    className="w-full h-11 px-4 pr-12 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    placeholder={config.has_password ? '••••••••' : 'Ingrese contraseña'}
                    data-testid="bridge-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {config.has_password && !config.password && (
                  <p className="text-xs text-gray-500 mt-1">
                    Deje vacío para mantener la contraseña actual
                  </p>
                )}
              </div>
            </div>

            {/* URL Base y Puerto OsmAnd */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Host OsmAnd/Traccar
                </label>
                <input
                  type="text"
                  value={config.osmand_host || '4sat.cl'}
                  onChange={(e) => setConfig({ ...config, osmand_host: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="4sat.cl"
                  data-testid="bridge-osmand-host-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Puerto OsmAnd
                </label>
                <input
                  type="number"
                  value={config.osmand_port || 6055}
                  onChange={(e) => setConfig({ ...config, osmand_port: parseInt(e.target.value) || 6055 })}
                  className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="6055"
                  data-testid="bridge-osmand-port-input"
                />
                <p className="text-xs text-gray-500 mt-1">Puerto TCP del protocolo OsmAnd (default: 6055)</p>
              </div>
            </div>

            {/* Configuración avanzada */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Tamaño de lote
                </label>
                <input
                  type="number"
                  value={config.batch_size}
                  onChange={(e) =>
                    setConfig({ ...config, batch_size: parseInt(e.target.value) || 10 })
                  }
                  className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  min="1"
                  max="100"
                  data-testid="bridge-batch-size-input"
                />
                <p className="text-xs text-gray-500 mt-1">Posiciones por envío</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Timeout (segundos)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={config.batch_timeout}
                  onChange={(e) =>
                    setConfig({ ...config, batch_timeout: parseFloat(e.target.value) || 5.0 })
                  }
                  className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  min="1"
                  max="60"
                  data-testid="bridge-timeout-input"
                />
                <p className="text-xs text-gray-500 mt-1">Espera máxima entre envíos</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Estado del Bridge
                </label>
                <button
                  onClick={() => setConfig({ ...config, enabled: !config.enabled })}
                  className={`w-full h-11 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    config.enabled
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-gray-800'
                      : 'bg-gray-600 hover:bg-gray-200 text-gray-800'
                  }`}
                  data-testid="bridge-toggle-btn"
                >
                  {config.enabled ? (
                    <>
                      <PlugsConnected size={20} />
                      Habilitado
                    </>
                  ) : (
                    <>
                      <Plugs size={20} />
                      Deshabilitado
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Botón guardar */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="h-11 px-6 rounded-lg bg-blue-600 hover:bg-blue-700 text-gray-800 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                data-testid="save-config-btn"
              >
                {saving ? (
                  <ArrowClockwise size={20} className="animate-spin" />
                ) : (
                  <FloppyDisk size={20} />
                )}
                Guardar Configuración
              </button>
            </div>
          </div>
        </div>

        {/* Streaming Externo - Per Device Config */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <VideoCamera size={24} className="text-blue-500" />
            Streaming Externo (4sat.cl)
          </h2>
          
          <p className="text-sm text-gray-600 mb-4">
            Configura el control de audio y canales permitidos para cada dispositivo en la página de video externo (/live/imei).
            El video siempre inicia muteado.
          </p>
          
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={videoConfigSearch}
                onChange={(e) => setVideoConfigSearch(e.target.value)}
                placeholder="Buscar dispositivo por nombre o IMEI..."
                className="w-full h-10 pl-10 pr-4 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-sm"
              />
            </div>
          </div>
          
          {/* Device list */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {devices
              .filter(d => {
                if (!videoConfigSearch) return true;
                const search = videoConfigSearch.toLowerCase();
                return (
                  d.name?.toLowerCase().includes(search) ||
                  d.imei?.toLowerCase().includes(search)
                );
              })
              .slice(0, 20)
              .map((device) => {
                const config = deviceVideoConfigs[device.imei] || {
                  allow_audio_control: false,
                  allowed_channels: [0, 1, 2, 3]
                };
                const hasCustomConfig = !!deviceVideoConfigs[device.imei];
                
                return (
                  <div
                    key={device.imei}
                    className={`p-4 rounded-lg border transition-colors ${
                      hasCustomConfig ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800 truncate">
                            {device.name || 'Sin nombre'}
                          </span>
                          {hasCustomConfig && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                              Personalizado
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{device.imei}</p>
                      </div>
                      
                      {/* Audio toggle */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Audio:</span>
                        <button
                          onClick={() => {
                            const newAudio = !config.allow_audio_control;
                            saveDeviceVideoConfig(device.imei, newAudio, config.allowed_channels);
                          }}
                          disabled={savingVideoConfig === device.imei}
                          className={`p-1.5 rounded transition-colors ${
                            config.allow_audio_control
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-500'
                          } ${savingVideoConfig === device.imei ? 'opacity-50' : ''}`}
                          title={config.allow_audio_control ? 'Audio visible' : 'Audio oculto'}
                        >
                          {config.allow_audio_control ? (
                            <ToggleRight size={20} weight="fill" />
                          ) : (
                            <ToggleLeft size={20} weight="fill" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Channels */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Canales:</span>
                      {[0, 1, 2, 3].map((ch) => {
                        const isEnabled = (config.allowed_channels || [0,1,2,3]).includes(ch);
                        return (
                          <button
                            key={ch}
                            onClick={() => {
                              const current = config.allowed_channels || [0, 1, 2, 3];
                              let newChannels;
                              if (isEnabled) {
                                newChannels = current.filter(c => c !== ch);
                                if (newChannels.length === 0) newChannels = [ch];
                              } else {
                                newChannels = [...current, ch].sort();
                              }
                              saveDeviceVideoConfig(device.imei, config.allow_audio_control, newChannels);
                            }}
                            disabled={savingVideoConfig === device.imei}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              isEnabled
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-500'
                            } ${savingVideoConfig === device.imei ? 'opacity-50' : ''}`}
                          >
                            CH{ch + 1}
                          </button>
                        );
                      })}
                      
                      {/* Reset button */}
                      {hasCustomConfig && (
                        <button
                          onClick={() => resetDeviceVideoConfig(device.imei)}
                          disabled={savingVideoConfig === device.imei}
                          className="ml-auto text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
                          title="Restablecer a valores por defecto"
                        >
                          <ArrowClockwise size={14} />
                          Reset
                        </button>
                      )}
                    </div>
                    
                    {/* Preview link */}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <a
                        href={`/live/${device.imei}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Eye size={14} />
                        Ver página de video: /live/{device.imei}
                      </a>
                    </div>
                  </div>
                );
              })}
            
            {devices.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <VideoCamera size={32} className="mx-auto mb-2" />
                <p>No hay dispositivos disponibles</p>
              </div>
            )}
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            Los dispositivos sin configuración personalizada usarán los valores por defecto (audio oculto, todos los canales habilitados).
          </p>
        </div>

        {/* Configuración de Puertos de Destino */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="port-config-section">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plugs size={24} className="text-orange-500" />
            Puertos de Destino
            <button
              onClick={loadPortConfig}
              className="ml-auto p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar configuración"
              disabled={loadingPortConfig}
            >
              <ArrowClockwise size={18} className={`text-gray-500 ${loadingPortConfig ? 'animate-spin' : ''}`} />
            </button>
          </h2>

          <p className="text-sm text-gray-600 mb-4">
            Configura a qué puerto enviar los datos de cada cuenta o dispositivo específico.
          </p>

          {/* Puerto por cuenta */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Puerto por Cuenta Tracksolid</h3>
            <div className="space-y-2">
              {portConfig.accountPorts.map((account) => (
                <div key={account.email} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{account.name || account.email}</p>
                    <p className="text-xs text-gray-500">{account.email}</p>
                  </div>
                  <select
                    value={account.port || portConfig.defaultPort}
                    onChange={(e) => updateAccountPort(account.email, e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm bg-white"
                    disabled={savingPortConfig}
                  >
                    {portConfig.availablePorts.map((p) => (
                      <option key={p.port} value={p.port}>{p.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Excepciones por IMEI */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Excepciones por IMEI
              <span className="text-xs font-normal text-gray-500 ml-2">
                (Sobreescribe el puerto de la cuenta)
              </span>
            </h3>

            {/* Lista de excepciones existentes */}
            {portConfig.imeiOverrides.length > 0 && (
              <div className="space-y-2 mb-4">
                {portConfig.imeiOverrides.map((override) => (
                  <div key={override.imei} className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex-1">
                      <p className="font-mono text-sm">{override.imei}</p>
                      {override.deviceName && (
                        <p className="text-xs text-gray-500">{override.deviceName}</p>
                      )}
                    </div>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm font-medium">
                      Puerto {override.port}
                    </span>
                    <button
                      onClick={() => removeImeiOverride(override.imei)}
                      className="p-1.5 hover:bg-red-100 rounded-lg transition-colors text-red-500"
                      title="Eliminar excepción"
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Agregar nueva excepción */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <select
                value={newImeiOverride.imei}
                onChange={(e) => setNewImeiOverride({ ...newImeiOverride, imei: e.target.value })}
                className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white"
              >
                <option value="">Seleccionar dispositivo...</option>
                {devices
                  .filter(d => !portConfig.imeiOverrides.find(o => o.imei === d.imei))
                  .map((device) => (
                    <option key={device.imei} value={device.imei}>
                      {device.imei} - {device.name || 'Sin nombre'}
                    </option>
                  ))}
              </select>
              <select
                value={newImeiOverride.port}
                onChange={(e) => setNewImeiOverride({ ...newImeiOverride, port: e.target.value })}
                className="px-3 py-2 border rounded-lg text-sm bg-white"
              >
                {portConfig.availablePorts.map((p) => (
                  <option key={p.port} value={p.port}>{p.name}</option>
                ))}
              </select>
              <button
                onClick={addImeiOverride}
                disabled={!newImeiOverride.imei || savingPortConfig}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Agregar
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            <strong>Prioridad:</strong> Excepción IMEI → Puerto de cuenta → Puerto por defecto (6055)
          </p>
        </div>

        {/* Pool de Telemetría */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="pool-config-section">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Database size={24} className="text-purple-500" />
            Pool de Telemetría
            <button
              onClick={() => { loadPoolStats(); loadPoolConfig(); }}
              className="ml-auto p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar estadísticas"
            >
              <ArrowClockwise size={18} className="text-gray-500" />
            </button>
          </h2>

          {/* Pool Stats */}
          {poolStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <div className="text-xs text-blue-600 font-medium mb-1">Total Registros</div>
                <div className="text-2xl font-bold text-blue-700">{poolStats.total_records?.toLocaleString() || 0}</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                <div className="text-xs text-amber-600 font-medium mb-1">Pendientes</div>
                <div className="text-2xl font-bold text-amber-700">{poolStats.pending_records?.toLocaleString() || 0}</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4">
                <div className="text-xs text-emerald-600 font-medium mb-1">Enviados</div>
                <div className="text-2xl font-bold text-emerald-700">{poolStats.sent_records?.toLocaleString() || 0}</div>
              </div>
              <div className={`bg-gradient-to-br rounded-lg p-4 ${poolStats.alert_triggered ? 'from-red-50 to-red-100' : 'from-gray-50 to-gray-100'}`}>
                <div className={`text-xs font-medium mb-1 ${poolStats.alert_triggered ? 'text-red-600' : 'text-gray-600'}`}>
                  Uso del Pool
                </div>
                <div className={`text-2xl font-bold ${poolStats.alert_triggered ? 'text-red-700' : 'text-gray-700'}`}>
                  {poolStats.size_mb?.toFixed(1) || 0} MB
                </div>
                <div className="text-xs text-gray-500">
                  de {(poolStats.max_size_mb / 1024).toFixed(1)} GB ({poolStats.usage_percent?.toFixed(1) || 0}%)
                </div>
              </div>
            </div>
          )}

          {/* Alert Banner */}
          {poolStats?.alert_triggered && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-red-700">
                <Warning size={20} weight="fill" />
                <span className="font-medium">¡Alerta! El pool está al {poolStats.usage_percent?.toFixed(1)}% de su capacidad</span>
              </div>
              <p className="text-sm text-red-600 mt-1">
                Considere aumentar el tamaño máximo o reducir el tiempo de retención.
              </p>
            </div>
          )}

          {/* Pool Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Gear size={16} />
              Configuración del Pool
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Max Pool Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tamaño Máximo (MB)
                </label>
                <input
                  type="number"
                  value={poolConfig.max_pool_size_mb}
                  onChange={(e) => setPoolConfig({ ...poolConfig, max_pool_size_mb: parseInt(e.target.value) || 8192 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="100"
                  max="16384"
                />
                <p className="text-xs text-gray-500 mt-1">Recomendado: 8192 MB (8 GB)</p>
              </div>

              {/* Retention Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retención (horas)
                </label>
                <input
                  type="number"
                  value={poolConfig.retention_hours}
                  onChange={(e) => setPoolConfig({ ...poolConfig, retention_hours: parseInt(e.target.value) || 24 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                  max="168"
                />
                <p className="text-xs text-gray-500 mt-1">Tiempo antes de limpiar registros enviados</p>
              </div>

              {/* Record Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Intervalo de Registro (seg)
                </label>
                <input
                  type="number"
                  value={poolConfig.record_interval_seconds}
                  onChange={(e) => setPoolConfig({ ...poolConfig, record_interval_seconds: parseInt(e.target.value) || 10 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="5"
                  max="60"
                />
                <p className="text-xs text-gray-500 mt-1">Frecuencia de envío a 4sat (ej: cada 10s)</p>
              </div>

              {/* Min Records Per Minute */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registros Mínimos/min
                </label>
                <input
                  type="number"
                  value={poolConfig.min_records_per_minute}
                  onChange={(e) => setPoolConfig({ ...poolConfig, min_records_per_minute: parseInt(e.target.value) || 6 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                  max="60"
                />
                <p className="text-xs text-gray-500 mt-1">Mínimo de registros por minuto por dispositivo</p>
              </div>

              {/* Alert Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Umbral de Alerta (%)
                </label>
                <input
                  type="number"
                  value={poolConfig.alert_threshold_percent}
                  onChange={(e) => setPoolConfig({ ...poolConfig, alert_threshold_percent: parseInt(e.target.value) || 10 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="5"
                  max="50"
                />
                <p className="text-xs text-gray-500 mt-1">Alertar cuando quede este % de espacio</p>
              </div>

              {/* Max Track Calls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Máx. Llamadas Track/ciclo
                </label>
                <input
                  type="number"
                  value={poolConfig.max_track_calls_per_cycle}
                  onChange={(e) => setPoolConfig({ ...poolConfig, max_track_calls_per_cycle: parseInt(e.target.value) || 10 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                  max="50"
                />
                <p className="text-xs text-gray-500 mt-1">Límite de API calls por ciclo (evita rate limit)</p>
              </div>
            </div>

            {/* Alert Types Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Bell size={16} />
                Tipos de Alertas a Incluir
              </label>
              <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {availableAlertTypes.map((alertType) => (
                    <label key={alertType} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={poolConfig.enabled_alert_types?.includes(alertType) || poolConfig.enabled_alert_types?.length === 0}
                        onChange={(e) => {
                          let newTypes = [...(poolConfig.enabled_alert_types || [])];
                          if (e.target.checked) {
                            if (!newTypes.includes(alertType)) {
                              newTypes.push(alertType);
                            }
                          } else {
                            newTypes = newTypes.filter(t => t !== alertType);
                          }
                          setPoolConfig({ ...poolConfig, enabled_alert_types: newTypes });
                        }}
                        className="rounded text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-gray-700">{alertTranslations[alertType] || alertType}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Si no selecciona ninguna, se incluirán todas las alertas. Las alertas siempre incluyen URL de evidencia.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={savePoolConfig}
                disabled={savingPool}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {savingPool ? (
                  <ArrowClockwise size={18} className="animate-spin" />
                ) : (
                  <FloppyDisk size={18} />
                )}
                Guardar Configuración
              </button>

              <button
                onClick={() => triggerPoolCleanup(false)}
                disabled={cleaningPool}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {cleaningPool ? (
                  <ArrowClockwise size={18} className="animate-spin" />
                ) : (
                  <Broom size={18} />
                )}
                Limpiar Antiguos
              </button>

              <button
                onClick={() => triggerPoolCleanup(true)}
                disabled={cleaningPool}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {cleaningPool ? (
                  <ArrowClockwise size={18} className="animate-spin" />
                ) : (
                  <Trash size={18} />
                )}
                Vaciar Pool
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="mb-2">
                  <strong>Pool de Telemetría:</strong> Almacena temporalmente los datos de posición antes de enviarlos a 4sat.cl.
                </p>
                <ul className="list-disc list-inside text-xs space-y-1 text-blue-700">
                  <li>Solo consulta historial de tracks para dispositivos con <strong>ignición ON</strong></li>
                  <li>Filtra registros cada N segundos + incluye TODAS las alertas con URL de evidencia</li>
                  <li>Limpia automáticamente registros enviados después del tiempo de retención</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Test Connection */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="bridge-test-section">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Play size={24} className="text-emerald-400" />
            Probar Conexión
          </h2>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="mb-2">
                  Esta prueba enviará la última ubicación GPS del dispositivo especificado a
                  4sat.cl para verificar que la integración funciona correctamente.
                </p>
                <p className="text-xs text-blue-300/70">
                  <strong>Nota:</strong> El servidor 4sat.cl debe tener habilitado el protocolo OsmAnd HTTP 
                  o los puertos TCP de Traccar para recibir datos. Si recibe error 404, contacte al 
                  administrador de 4sat.cl.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-800 mb-2">
                IMEI del dispositivo de prueba
              </label>
              <input
                type="text"
                value={config.test_imei}
                onChange={(e) => setConfig({ ...config, test_imei: e.target.value })}
                className="w-full h-11 px-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                placeholder="864993060374437"
                data-testid="test-imei-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleTest}
                disabled={testing || !config.test_imei}
                className="h-11 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-gray-800 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
                data-testid="test-connection-btn"
              >
                {testing ? (
                  <>
                    <ArrowClockwise size={20} className="animate-spin" />
                    Probando...
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Ejecutar Prueba
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`mt-4 p-4 rounded-lg border ${
                testResult.success
                  ? 'bg-emerald-900/30 border-emerald-700/50'
                  : 'bg-red-900/30 border-red-700/50'
              }`}
              data-testid="test-result"
            >
              <div className="flex items-start gap-3">
                {testResult.success ? (
                  <CheckCircle size={24} className="text-emerald-400 shrink-0" />
                ) : (
                  <XCircle size={24} className="text-red-400 shrink-0" />
                )}
                <div>
                  <p
                    className={`font-medium ${
                      testResult.success ? 'text-emerald-300' : 'text-red-300'
                    }`}
                  >
                    {testResult.message}
                  </p>
                  {testResult.data && (
                    <div className="mt-2 text-sm text-gray-500 font-mono">
                      <p>IMEI: {testResult.data.imei}</p>
                      <p>
                        Lat: {testResult.data.lat}, Lng: {testResult.data.lng}
                      </p>
                      <p>Velocidad: {testResult.data.speed} km/h</p>
                    </div>
                  )}
                  {testResult.step && !testResult.success && (
                    <p className="mt-1 text-sm text-gray-500">Paso fallido: {testResult.step}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tracksolid Accounts Management */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="tracksolid-accounts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Users size={24} className="text-indigo-500" />
              Cuentas Tracksolid Conectadas
            </h2>
            <div className="flex gap-2">
              <button
                onClick={loadTracksolidAccounts}
                disabled={loadingAccounts}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
                title="Actualizar lista"
              >
                <ArrowClockwise size={18} className={loadingAccounts ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setShowAddAccount(!showAddAccount)}
                className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-gray-800 font-medium transition-colors flex items-center gap-2 text-sm"
              >
                <UserPlus size={18} />
                Agregar
              </button>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-indigo-600 mt-0.5 shrink-0" />
              <div className="text-sm text-indigo-800">
                <p>
                  Agrega múltiples cuentas de Tracksolid para ver todos los dispositivos 
                  de diferentes sub-usuarios en una sola plataforma. Los dispositivos se 
                  agregarán automáticamente al listado general.
                </p>
              </div>
            </div>
          </div>

          {/* Add Account Form */}
          {showAddAccount && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
              <h3 className="font-medium mb-3 text-gray-800">Nueva Cuenta Tracksolid</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Nombre (opcional)</label>
                  <input
                    type="text"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                    placeholder="Ej: Flota Norte"
                    className="w-full h-10 px-3 rounded-lg bg-gray-100 border border-gray-200 text-gray-800 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Email/Usuario *</label>
                  <input
                    type="email"
                    value={newAccount.email}
                    onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                    placeholder="usuario@email.com"
                    className="w-full h-10 px-3 rounded-lg bg-gray-100 border border-gray-200 text-gray-800 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Contraseña *</label>
                  <input
                    type="password"
                    value={newAccount.password}
                    onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full h-10 px-3 rounded-lg bg-gray-100 border border-gray-200 text-gray-800 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowAddAccount(false)}
                  className="h-9 px-4 rounded-lg bg-gray-200 hover:bg-gray-200/80 text-gray-800 font-medium transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddAccount}
                  disabled={addingAccount}
                  className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-gray-800 font-medium transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  {addingAccount ? (
                    <ArrowClockwise size={16} className="animate-spin" />
                  ) : (
                    <UserPlus size={16} />
                  )}
                  Agregar Cuenta
                </button>
              </div>
            </div>
          )}

          {/* Accounts List */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500 flex items-center justify-between">
              <span>Cuentas ({tracksolidAccounts.length})</span>
              <span>
                {tracksolidAccounts.filter(a => a.enabled).length} activas / 
                {tracksolidAccounts.reduce((sum, a) => sum + (a.device_count || 0), 0)} dispositivos
              </span>
            </div>
            <div className="divide-y divide-border">
              {loadingAccounts ? (
                <div className="p-8 text-center text-gray-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Cargando cuentas...
                </div>
              ) : tracksolidAccounts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No hay cuentas Tracksolid configuradas</p>
                  <p className="text-sm mt-1">Agrega una cuenta para comenzar</p>
                </div>
              ) : (
                tracksolidAccounts.map((account) => (
                  <div
                    key={account.email}
                    className={`px-4 py-3 flex items-center justify-between ${
                      !account.enabled ? 'bg-gray-50 opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${
                        account.status === 'active' ? 'bg-green-500' :
                        account.status === 'validation_warning' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`} />
                      <div>
                        <div className="font-medium text-gray-800">
                          {account.name || account.email}
                        </div>
                        <div className="text-xs text-gray-500">
                          {account.email} • {account.device_count || 0} dispositivos
                          {account.last_sync && (
                            <> • Última sync: {new Date(account.last_sync).toLocaleString()}</>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleAccount(account.email, account.enabled)}
                        className={`p-2 rounded-lg transition-colors ${
                          account.enabled 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-500 hover:bg-gray-100'
                        }`}
                        title={account.enabled ? 'Deshabilitar' : 'Habilitar'}
                      >
                        {account.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                      </button>
                      <button
                        onClick={() => handleDeleteAccount(account.email)}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                        title="Eliminar cuenta"
                      >
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Device MediaUrl Configuration */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="device-media-config">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Camera size={24} className="text-purple-600" />
              Envío de URL de Evidencia
            </h2>
            <button
              onClick={loadDevices}
              disabled={loadingDevices}
              className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-colors"
              title="Actualizar lista"
            >
              <ArrowClockwise size={18} className={loadingDevices ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-purple-600 mt-0.5 shrink-0" />
              <div className="text-sm text-purple-800">
                <p>
                  Selecciona los dispositivos que enviarán la URL de evidencia (foto/video) 
                  cuando se detecte una alerta DMS. Los dispositivos deben tener activada 
                  la captura de foto/video por alerta en Tracksolid.
                </p>
              </div>
            </div>
          </div>

          {/* Search and bulk actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                placeholder="Buscar por nombre o IMEI..."
                className="w-full h-10 pl-10 pr-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-200 text-gray-800 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <CheckSquare size={16} />
                Todos
              </button>
              <button
                onClick={deselectAll}
                className="h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-200 text-gray-800 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Square size={16} />
                Ninguno
              </button>
            </div>
          </div>

          {/* Device list */}
          <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500 flex items-center justify-between">
              <span>Dispositivos ({filteredDevices.length})</span>
              <span>{selectedDevices.size} seleccionados</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {loadingDevices ? (
                <div className="p-8 text-center text-gray-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Cargando dispositivos...
                </div>
              ) : filteredDevices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No se encontraron dispositivos
                </div>
              ) : (
                filteredDevices.map((device) => (
                  <div
                    key={device.imei}
                    onClick={() => toggleDevice(device.imei)}
                    className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors border-b border-gray-200/50 last:border-b-0 ${
                      selectedDevices.has(device.imei)
                        ? 'bg-purple-100 hover:bg-purple-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedDevices.has(device.imei)
                        ? 'bg-purple-500 border-purple-500'
                        : 'border-gray-500'
                    }`}>
                      {selectedDevices.has(device.imei) && (
                        <CheckCircle size={14} weight="bold" className="text-gray-800" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 truncate">{device.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{device.imei}</div>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${device.online ? 'bg-emerald-400' : 'bg-gray-500'}`} 
                         title={device.online ? 'En línea' : 'Desconectado'} />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              onClick={saveDevicesConfig}
              disabled={savingDevices}
              className="h-11 px-6 rounded-lg bg-purple-600 hover:bg-purple-700 text-gray-800 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {savingDevices ? (
                <ArrowClockwise size={20} className="animate-spin" />
              ) : (
                <FloppyDisk size={20} />
              )}
              Guardar Configuración de Evidencia
            </button>
          </div>
        </div>

        {/* Device Live Video Configuration */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="device-live-video-config">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <VideoCamera size={24} className="text-cyan-600" />
              Envío de URL de Video en Vivo
            </h2>
            <button
              onClick={loadDevices}
              disabled={loadingDevices}
              className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-900 transition-colors"
              title="Actualizar lista"
            >
              <ArrowClockwise size={18} className={loadingDevices ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-cyan-600 mt-0.5 shrink-0" />
              <div className="text-sm text-cyan-800">
                <p>
                  Selecciona los dispositivos que enviarán la URL de streaming en vivo 
                  cuando se detecte una alerta DMS. Esto permite ver la cámara del vehículo 
                  en tiempo real desde 4sat.cl.
                </p>
              </div>
            </div>
          </div>

          {/* Search and bulk actions for Live Video */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={liveVideoSearch}
                onChange={(e) => setLiveVideoSearch(e.target.value)}
                placeholder="Buscar por nombre o IMEI..."
                className="w-full h-10 pl-10 pr-4 rounded-lg bg-gray-200 border border-gray-200 text-gray-800 placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAllLiveVideo}
                className="h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-200 text-gray-800 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <CheckSquare size={16} />
                Todos
              </button>
              <button
                onClick={deselectAllLiveVideo}
                className="h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-200 text-gray-800 text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Square size={16} />
                Ninguno
              </button>
            </div>
          </div>

          {/* Device list for Live Video */}
          <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
            <div className="bg-gray-100 px-4 py-2 text-sm text-gray-500 flex items-center justify-between">
              <span>Dispositivos ({filteredLiveVideoDevices.length})</span>
              <span>{selectedLiveVideo.size} seleccionados</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {loadingDevices ? (
                <div className="p-8 text-center text-gray-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Cargando dispositivos...
                </div>
              ) : filteredLiveVideoDevices.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No se encontraron dispositivos
                </div>
              ) : (
                filteredLiveVideoDevices.map((device) => (
                  <div
                    key={device.imei}
                    onClick={() => toggleLiveVideo(device.imei)}
                    className={`px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors border-b border-gray-200/50 last:border-b-0 ${
                      selectedLiveVideo.has(device.imei)
                        ? 'bg-cyan-100 hover:bg-cyan-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      selectedLiveVideo.has(device.imei)
                        ? 'bg-cyan-500 border-cyan-500'
                        : 'border-gray-500'
                    }`}>
                      {selectedLiveVideo.has(device.imei) && (
                        <CheckCircle size={14} weight="bold" className="text-gray-800" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 truncate">{device.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{device.imei}</div>
                    </div>
                    <div className={`w-2 h-2 rounded-full ${device.online ? 'bg-emerald-400' : 'bg-gray-500'}`} 
                         title={device.online ? 'En línea' : 'Desconectado'} />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Save button for Live Video */}
          <div className="flex justify-end">
            <button
              onClick={saveDevicesConfig}
              disabled={savingDevices}
              className="h-11 px-6 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-gray-800 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {savingDevices ? (
                <ArrowClockwise size={20} className="animate-spin" />
              ) : (
                <FloppyDisk size={20} />
              )}
              Guardar Configuración de Video en Vivo
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BridgeSettings;
